import React, { Suspense, useEffect, useState } from "react";
import { Provider } from "react-redux";
import {
  Link,
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import io from "socket.io-client";
// defaults to localStorage for web
import { Theme247 } from "./styles/Theme247";

import { CssBaseline } from "@mui/material";
import { config_UPTIME } from "./Constants";
import { GlobalCss } from "./styles/GlobalCss";

import MachineOverviewPage from "./pages/MachineOverviewPage/MachineOverviewPage";
import NotificationDetailsPage from "./pages/NotificationDetailsPage/NotificationDetailsPage";

import ProfilePage from "./pages/ProfilePage/ProfilePage";

import store from "./store";
import InitialState from "./pages/NewDashboardPage/component/InitialState";
import CreateSite from "./pages/CreateSite";
import CreateEquipment from "./pages/CreateEquipment";
import CreateArea from "./pages/CreateArea";
import CreateEquipmentPool from "./pages/CreateEquipmentPool";
import Users from "./pages/Users/Users";

import OrderSubscriptionPage from "./pages/OrderSubscriptionPage/OrderSubscriptionPage";
import OrderSubscriptionStep2 from "./pages/OrderSubscriptionPage/OrderSubscriptionStep2";
import OrderSubscriptionStep3 from "./pages/OrderSubscriptionPage/OrderSubscriptionStep3";
import SignUpPage from "./pages/SignUpPage/SignUpPage";
import HelpAndFeedbackPage from "./pages/HelpAndFeedbackPage/HelpAndFeedbackPage";
import { logout } from "./actions/auth";
import TemplateSkeliton from "./pages/MachineOverviewPage/_tabs/Maintenance/components/TemplateSkeliton";
import AufgabePlanung from "./pages/AufgabePlanung";
import GlobalSnackbar from "./components/GlobalSnackbar";

const CreateMachineTypePage = React.lazy(() =>
  import("./pages/CreateMachineTypePage/CreateMachineTypePage")
);

const OpenNotifications = React.lazy(() =>
  import("./pages/DashboardPage/components/OpenNotifications")
);

const AuthRoute = React.lazy(() => import("./components/AuthRoute"));

const ScopeHeader = React.lazy(() => import("./components/ScopeHeader"));

const LoginPage = React.lazy(() => import("./pages/LoginPage/LoginPage"));
const NewDashboardPage = React.lazy(() =>
  import("./pages/NewDashboardPage/newdashboard")
);

const MessagesPage = React.lazy(() =>
  import("./pages/MessagesPage/MessagesPage")
);
const EditNotificationPage = React.lazy(() =>
  import("./pages/EditNotificationPage/EditNotificationPage")
);
const AddUserPage = React.lazy(() => import("./pages/AddUserPage/AddUserPage"));
const AdminPage = React.lazy(() => import("./pages/AdminPage/AdminPage"));
const MachinesPage = React.lazy(() =>
  import("./pages/MachinesPage/MachinesPage")
);

const SitePage = React.lazy(() => import("./pages/SitePage/SitePage"));
const CreateSitePage = React.lazy(() =>
  import("./pages/CreateSitePage/CreateSitePage")
);

const CreateAreaPage = React.lazy(() =>
  import("./pages/CreateAreaPage/CreateAreaPage")
);
// const DashboardPage = React.lazy(() => import("./pages/DashboardPage/DashboardPage"))
const SuperUserDashboardPage = React.lazy(() =>
  import("./pages/SuperUserDashboardPage/SuperUserDashboardPage")
);

const CreateMachinePage = React.lazy(() =>
  import("./pages/CreateMachinePage/CreateMachinePage")
);
const CreateMachinePoolPage = React.lazy(() =>
  import("./pages/CreateMachinePoolPage/CreateMachinePoolPage")
);

const ClientsPage = React.lazy(() => import("./pages/ClientsPage/ClientsPage"));
const CreateClientPage = React.lazy(() =>
  import("./pages/ClientsPage/CreateClientPage")
);

const MainLayout = React.lazy(() => import("./components/MainLayout"));

const CreateWorkflowStep = React.lazy(() =>
  import("./pages/WorkFlowSteps/CreateWorkflowStep")
);

const CreateMaintenanceTask = React.lazy(() =>
  import(
    "./pages/MachineOverviewPage/_tabs/Maintenance/components/CreateMaintenanceTask"
  )
);
const CreateReport = React.lazy(() => import("./pages/protocol/protocol"));

const NewCreateReport = React.lazy(() =>
  import("./pages/protocol/NewProtocol")
);

const MaintenanceDashboard = React.lazy(() =>
  import("./pages/MaintenanceDashboard/MaintenanceDashoard")
);
const SetPassword = React.lazy(() => import("./pages/PaswordPage/SetPassword"));
const ResetPassword = React.lazy(() =>
  import("./pages/PaswordPage/ResetPassword")
);
const MbDashboard = React.lazy(() => import("./pages/MbDashboard/MbDashboard"));
const MachineTypeOverviewPage = React.lazy(() =>
  import("./pages/MachineTypeOverviewPage/MachineTypeOverviewPage")
);
const MbAdminOverviewPage = React.lazy(() =>
  import("./pages/MbAdminOverviewPage/MbAdminOverviewPage")
);
const CreateBrandPage = React.lazy(() =>
  import("./pages/MbAdminOverviewPage/_tabs/Brands/CreateBrandPage")
);
const CreateEquipmentClassPage = React.lazy(() =>
  import(
    "./pages/MbAdminOverviewPage/_tabs/EquipmentClasses/CreateEquipmentClassPage"
  )
);
const EquipmentTypesMachinesPage = React.lazy(() =>
  import("./pages/EquipmentTypesMachinesPage/EquipmentTypesMachinesPage")
);
const Welcome = React.lazy(() => import("./pages/FreeUserWelcomePage/welcome"));
const SiteAreaEqs = React.lazy(() => import("./components/SiteAreaEqs"));
const ExportPage = React.lazy(() => import("./pages/ExportPage/ExportPage"));
const Newcalender = React.lazy(() => import("./pages/Newcalender/Newcalender"));

const TwoFa = React.lazy(() => import("./pages/TwoFa/twoFa"));

export const socket = io(
  config_UPTIME.url.SOCKET_URL,
  {
    reconnection: true, // whether to reconnect automatically
    reconnectionAttempts: 1, // number of reconnection attempts before giving up
    reconnectionDelay: 1000, // how long to initially wait before attempting a new reconnection
    reconnectionDelayMax: 5000, // maximum amount of time to wait between reconnection attempts. Each attempt increases the reconnection delay by 2x along with a randomization factor
    randomizationFactor: 0.5,
  },
  { transports: ["pooling", "websocket"] }
).connect();

function App() {
  const AUTO_LOGOUT_TABS_KEY = "__uptime_active_tabs__";
  const AUTO_LOGOUT_PENDING_KEY = "__uptime_pending_logout__";
  const AUTO_LOGOUT_TAB_ID_KEY = "__uptime_tab_id__";
  const AUTO_LOGOUT_NAV_FLAG_KEY = "__uptime_nav_flag__";

  // const navigate = useNavigate()

  useEffect(() => {
    const shouldAutoLogout = (autoLogoutValue) => autoLogoutValue !== false;

    const readTabs = () => {
      try {
        const raw = localStorage.getItem(AUTO_LOGOUT_TABS_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    };

    const writeTabs = (tabs) => {
      localStorage.setItem(AUTO_LOGOUT_TABS_KEY, JSON.stringify(tabs));
    };

    const getOrCreateTabId = () => {
      let tabId = sessionStorage.getItem(AUTO_LOGOUT_TAB_ID_KEY);
      if (!tabId) {
        tabId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(AUTO_LOGOUT_TAB_ID_KEY, tabId);
      }
      return tabId;
    };

    const registerTab = (tabId) => {
      const tabs = readTabs();
      if (!tabs.includes(tabId)) {
        tabs.push(tabId);
        writeTabs(tabs);
      }
    };

    const unregisterTab = (tabId) => {
      const tabs = readTabs().filter((id) => id !== tabId);
      writeTabs(tabs);
      return tabs.length;
    };

    const wasReloadInSameTab =
      sessionStorage.getItem(AUTO_LOGOUT_NAV_FLAG_KEY) === "1";
    sessionStorage.removeItem(AUTO_LOGOUT_NAV_FLAG_KEY);

    // If the previous session ended with all tabs closed and auto logout enabled,
    // clear auth data on the next real open, but do not on simple refresh.
    if (!wasReloadInSameTab && localStorage.getItem(AUTO_LOGOUT_PENDING_KEY)) {
      localStorage.removeItem(AUTO_LOGOUT_PENDING_KEY);
      store.dispatch(logout());
    } else if (wasReloadInSameTab) {
      localStorage.removeItem(AUTO_LOGOUT_PENDING_KEY);
    }

    const tabId = getOrCreateTabId();
    registerTab(tabId);

    let leaveHandled = false;
    const handleLeave = () => {
      if (leaveHandled) return;
      leaveHandled = true;

      sessionStorage.setItem(AUTO_LOGOUT_NAV_FLAG_KEY, "1");

      const tabsRemaining = unregisterTab(tabId);
      if (tabsRemaining === 0 && shouldAutoLogout(store.getState().autoLogout)) {
        localStorage.setItem(
          AUTO_LOGOUT_PENDING_KEY,
          JSON.stringify({ ts: Date.now() }),
        );
      } else {
        localStorage.removeItem(AUTO_LOGOUT_PENDING_KEY);
      }
    };

    window.addEventListener("beforeunload", handleLeave);
    window.addEventListener("pagehide", handleLeave);
    return () => {
      window.removeEventListener("beforeunload", handleLeave);
      window.removeEventListener("pagehide", handleLeave);
    };
  }, []);

  const getStateAutoLogout = () => {};

  const refs = Array(6)
    .fill(null)
    .map(() => React.createRef());

  const PageNotFound = () => {
    useEffect(() => {
      document.body.classList.add("page404-active");
      return () => {
        document.body.classList.remove("page404-active");
      };
    }, []);

    return (
      <div className="Page404">
        <div className="Page404_glow Page404_glowLeft" />
        <div className="Page404_glow Page404_glowRight" />
        <div className="Page404_content">
          <p className="Page404_kicker">Lost In Production Space</p>
          <h1 className="Page404_code">404</h1>
          <h2 className="Page404_title">Diese Seite existiert nicht</h2>
          <p className="Page404_subtitle">
            Der Link ist ungültig oder die Seite wurde verschoben.
          </p>
          <div className="Page404_actions">
            <Link to={"/dashboard"} className="Page404_btn Page404_btnPrimary">
              Zum Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // render your app here
  return (
    <ThemeProvider theme={Theme247}>
      <CssBaseline />
      <GlobalCss />

      <GlobalSnackbar />
      
      <Router>
        <Routes>
          <Route
            path="/signup"
            element={
              <AuthRoute type="guest">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <SignUpPage />
                </MainLayout>
              </AuthRoute>
            }
          />
          <Route
            path="/login"
            element={
              <AuthRoute type="guest">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <LoginPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/welcome"
            element={
              <AuthRoute type="guest">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <Welcome />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg1.webp"
                    txtColor="#FFFFFF"
                    title="Dashboard"
                    id="card_dashboard_main"
                    dataTestId="card_dashboard_main"
                    description="Das Dashboard zeigt Ihnen auf, welche Ihrer Equipments Auffälligkeiten aufweisen, damit Sie umgehend darauf reagieren können."
                  />
                  <SiteAreaEqs />
                  <NewDashboardPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/initial-state"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  {/* <ScopeHeader bgColor="#0E3F51" bgImage="../images/bg1.webp" txtColor="#FFFFFF" title="Dashboard" description="Das Dashboard zeigt Ihnen auf, welche Ihrer Maschinen Auffälligkeiten aufweien, damit Sie umgehend darauf reagieren können." /> */}

                  {/* <SiteAreaEqs /> */}
                  <InitialState />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/machines"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    linearGradient="rgba(0, 0, 0, 0.4)"
                    bgImage="../images/bg5.webp"
                    txtColor="#FFFFFF"
                    title="Equipment"
                    description="Auf dieser Seite verwalten Sie Ihre Equipment Pools und deren zugeordnetes Equipment (Maschinen, Geräten, etc.) Es werden nur Equipments angezeit die einem Pool zugeordnet sind."
                  />

                  <SiteAreaEqs />
                  <MachinesPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-machine"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  {/* <CreateMachinePage /> */}
                  <CreateEquipment />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-machine"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateMachinePage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-machine-pool"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateMachinePoolPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-machine-pool"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateMachinePoolPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-notification"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <EditNotificationPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-notification"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <EditNotificationPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ProfilePage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/users"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <Users />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <MessagesPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/add-user"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <AddUserPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-user"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <AddUserPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/machine-overview"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <MachineOverviewPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/export"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg1.webp"
                    txtColor="#FFFFFF"
                    title="Export"
                    description="Hier können Sie in 247UpTime gespeicherte Daten wie Meldungen und Zählerstände exportieren."
                  />
                  <ExportPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-site"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateSite />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-area"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateArea />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-equipment"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateEquipment />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-equipment-pool"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateEquipmentPool />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/notifications-dashboard"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg6.webp"
                    txtColor="#FFFFFF"
                    title="Offene Meldungen"
                    description="Hier verschaffen Sie sich einen Überblick über aktuelle Vorkommnisse oder können neue Meldungen erstellen, über die Ihre Kollegen benachrichtigt werden."
                  />

                  <OpenNotifications />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/super"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <SuperUserDashboardPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/notification-details"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <NotificationDetailsPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <AdminPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            exact
            path="/sites"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg12.webp"
                    txtColor="#FFFFFF"
                    title="Werke"
                    description="Hier verwalten Sie Ihre Werke mit ihren zugehörigen Teilbereichen."
                  />

                  <SitePage />
                </MainLayout>
              </AuthRoute>
            }
          />
          <Route
            path="/sites/:no_sites"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg12.webp"
                    txtColor="#FFFFFF"
                    title="Werke"
                    description="Hier verwalten Sie Ihre Werke mit ihren zugehörigen Teilbereichen."
                  />

                  <SitePage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-site"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateSitePage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-site"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <SitePage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-pool"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateSitePage />
                </MainLayout>
              </AuthRoute>
            }
          />

          {/* <Route path="/edit-area" element={
            <AuthRoute type="private" >
              <MainLayout getStateAutoLogout={getStateAutoLogout} >
                <SitePage />
              </MainLayout>
            </AuthRoute>
          }
          /> */}

          <Route
            path="/clients"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ClientsPage />
                </MainLayout>
              </AuthRoute>
            }
          />
          <Route
            path="/create-client"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateClientPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-workflow-step"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateWorkflowStep />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-workflow-step"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateWorkflowStep />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-faq"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateWorkflowStep />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-faq"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateWorkflowStep />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-maintenance-task"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateMaintenanceTask />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-maintenance-task"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateMaintenanceTask />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/template-skeliton"
            element={
              <MainLayout getStateAutoLogout={getStateAutoLogout}>
                <TemplateSkeliton />
              </MainLayout>
            }
          />

          <Route
            path="/create-report"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateReport />
                </MainLayout>
              </AuthRoute>
            }
          />

          {/* <Route
            path="/create-new-report"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <NewCreateReport />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-new-report"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <NewCreateReport />
                </MainLayout>
              </AuthRoute>
            }
          /> */}

          <Route
            path="/edit-report"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateReport />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/maintenance-dashboard"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg9.webp"
                    txtColor="#FFFFFF"
                    title="Geplante Wartungen"
                    description="Diese Ansicht hilft Ihnen dabei, wiederkehrende Wartungen und Inspektionen im Blick zu behalten und einzuplanen."
                  />
                  <SiteAreaEqs />
                  <Newcalender />
                  {/*  <MaintenanceDashboard /> */}
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/aufgabeplanung"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg2.webp"
                    txtColor="#FFFFFF"
                    title="Aufgabenplanung"
                    description="Ordnen Sie offene Meldungen einzelnen Mitarbeitern zu und behalten Sie so den Überblick."
                  />
                  <AufgabePlanung />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/new-calender"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <SiteAreaEqs />
                  <Newcalender />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/2fa"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <TwoFa refs={refs.current} />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/mb-dashboard"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <MbDashboard />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/equipmenttype-overview"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <MachineTypeOverviewPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-equipmenttype"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateMachineTypePage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/mb-admin"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <MbAdminOverviewPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-brand"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateBrandPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-brand"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateBrandPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/create-equipment-class"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateEquipmentClassPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/edit-equipment-class"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <CreateEquipmentClassPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/types-machines"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <EquipmentTypesMachinesPage />
                </MainLayout>
              </AuthRoute>
            }
          />

          <Route
            path="/set-password/:token"
            element={
              <MainLayout>
                {" "}
                <SetPassword />{" "}
              </MainLayout>
            }
          />

          <Route
            path="/reset-password"
            element={
              <MainLayout getStateAutoLogout={getStateAutoLogout}>
                <ResetPassword />
              </MainLayout>
            }
          />

          <Route
            path="/OrderSubscription"
            element={
              <AuthRoute type="private">
                <OrderSubscriptionPage />
              </AuthRoute>
            }
          />
          <Route
            path="/HelpAndFeedbackPage"
            element={
              <AuthRoute type="private">
                <MainLayout getStateAutoLogout={getStateAutoLogout}>
                  <ScopeHeader
                    bgColor="#0E3F51"
                    bgImage="../images/bg9.webp"
                    txtColor="#FFFFFF"
                    title="Hilfe & Feedback"
                    description="Lesen Sie hier über die Funktionalitäten von 247UpTime nach oder hinterlassen Sie uns Ihr Feedback zur Software."
                  />
                  <HelpAndFeedbackPage />
                </MainLayout>{" "}
              </AuthRoute>
            }
          />

          {/* <Route
            path="/OrderSubscriptionstep2"
            element={
              <MainLayout getStateAutoLogout={getStateAutoLogout}>
                <OrderSubscriptionStep2 />
              </MainLayout>
            }
          />
          <Route
            path="/OrderSubscriptionstep3"
            element={
              <MainLayout getStateAutoLogout={getStateAutoLogout}>
                <OrderSubscriptionStep3 />
              </MainLayout>
            }
          /> */}
          <Route path="/" element={<Navigate to="/login" replace={true} />} />
          {/* <Route
            index 
            path="/"
            element={<Navigate to="/signup" replace={true} />}
          /> */}
          <Route exact path="*" element={<PageNotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

const UptimeWeb = () => {
  return (
    <Provider store={store}>
      <Suspense fallback={<div>loading...</div>}>
        <App />
      </Suspense>
    </Provider>
  );
};

export default UptimeWeb;
